import * as React from "react";

import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/cn";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

interface ResponsiveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

/**
 * Адаптивная обёртка: на mobile (<md) рендерит Sheet с side="bottom",
 * на desktop — обычный Dialog. Совместима по слотам через
 * ResponsiveDialogContent / Header / Footer / Title / Description.
 */
const ResponsiveDialogContext = React.createContext<{ isMobile: boolean }>({
  isMobile: false,
});

export const ResponsiveDialog = ({ open, onOpenChange, children }: ResponsiveDialogProps) => {
  const isMobile = useIsMobile();

  const ctxValue = React.useMemo(() => ({ isMobile }), [isMobile]);

  if (isMobile) {
    return (
      <ResponsiveDialogContext.Provider value={ctxValue}>
        <Sheet
          open={open}
          onOpenChange={onOpenChange}
        >
          {children}
        </Sheet>
      </ResponsiveDialogContext.Provider>
    );
  }

  return (
    <ResponsiveDialogContext.Provider value={ctxValue}>
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
      >
        {children}
      </Dialog>
    </ResponsiveDialogContext.Provider>
  );
};

export const ResponsiveDialogContent = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DialogContent>) => {
  const { isMobile } = React.useContext(ResponsiveDialogContext);

  if (isMobile) {
    return (
      <SheetContent
        side="bottom"
        className={cn(
          "rounded-t-mds max-h-[90dvh] gap-4 overflow-y-auto p-4 pb-[max(1rem,env(safe-area-inset-bottom))]",
          className
        )}
      >
        {children}
      </SheetContent>
    );
  }

  return (
    <DialogContent
      className={className}
      {...props}
    >
      {children}
    </DialogContent>
  );
};

export const ResponsiveDialogHeader = ({
  className,
  ...props
}: React.ComponentProps<"div">) => {
  const { isMobile } = React.useContext(ResponsiveDialogContext);

  if (isMobile) {
    return (
      <SheetHeader
        className={cn("p-0", className)}
        {...props}
      />
    );
  }

  return (
    <DialogHeader
      className={className}
      {...props}
    />
  );
};

export const ResponsiveDialogFooter = ({
  className,
  ...props
}: React.ComponentProps<"div">) => {
  const { isMobile } = React.useContext(ResponsiveDialogContext);

  if (isMobile) {
    return (
      <SheetFooter
        className={cn("mt-2 flex-col-reverse gap-2 p-0", className)}
        {...props}
      />
    );
  }

  return (
    <DialogFooter
      className={className}
      {...props}
    />
  );
};

export const ResponsiveDialogTitle = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogTitle>) => {
  const { isMobile } = React.useContext(ResponsiveDialogContext);

  if (isMobile) {
    return (
      <SheetTitle
        className={cn("text-lg leading-none font-semibold", className)}
        {...props}
      />
    );
  }

  return (
    <DialogTitle
      className={className}
      {...props}
    />
  );
};

export const ResponsiveDialogDescription = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogDescription>) => {
  const { isMobile } = React.useContext(ResponsiveDialogContext);

  if (isMobile) {
    return (
      <SheetDescription
        className={className}
        {...props}
      />
    );
  }

  return (
    <DialogDescription
      className={className}
      {...props}
    />
  );
};
